<script setup lang="ts">
import type { HybridlyFormData } from '@/@types/global';

import { useChatBot } from '@/domains/chat/composables/useChatBot';
import { usePageMeta } from '@/hooks/usePageMeta';

import Alert from '@/components/alert/Alert.vue';
import DangerDialog from '@/components/dialog/DangerDialog.vue';
import Dialog from '@/components/dialog/Dialog.vue';

import Check from '@/icons/line/check.svg';
import TimesCircle from '@/icons/line/times-circle.svg';

const props = defineProps<{
  aiAdviserMessage?: string;
  form: HybridlyFormData<unknown>;
  dataRequestType?: string;
}>();

watch(props.form, (form) => {
  if (form.successful) {
    router.dialog.close({ data: { consumeDeferredToasts: true } });
  }
});

const { chatBotName, enableChatBotForStudents } = useChatBot();
const { title } = usePageMeta();
const { show, closeLocally } = useDialog();
const [isCancelDialogOpen, toggleCancelDialog] = useToggle(false);

const computedAiAdviserMessage: ComputedRef<string | undefined> = computed(() => {
  if (props.aiAdviserMessage) {
    return props.aiAdviserMessage;
  }
  if (props.dataRequestType && chatBotName.value) {
    return `Hi ${chatBotName.value}, I have a question about ${props.dataRequestType}.`;
  }
  return undefined;
});

function handleFormSubmit() {
  props.form.submit();
}

function handleToggleCancelDialog() {
  if (props.form.isDirty) {
    toggleCancelDialog();
    return;
  }
  closeLocally();
}

function handleCancelDialogConfirm() {
  toggleCancelDialog();
  closeLocally();
}
</script>


					<script lang="ts">
					import __hybridly_layout_0 from '~/resources/layouts/student-parent.vue';
					export default { layout: [__hybridly_layout_0] }
					</script>
					<template>
				
  <Dialog
    :aiAdviserMessage="computedAiAdviserMessage"
    :cancelButtonDisabled="!form.isDirty"
    :confirmButtonIcon="Check"
    :isLoading="form.processing"
    :isOpen="show"
    :showAiAdviser="enableChatBotForStudents"
    :title="title"
    cancelButtonLabel="Reset"
    confirmButtonLabel="Save changes"
    @onCancel="form.reset"
    @onClose="handleToggleCancelDialog"
    @onConfirm="handleFormSubmit"
  >
    <template #form>
      <slot name="form" :form="form" />
    </template>
    <template v-if="form.hasErrors" #footerHelperText>
      <Alert
        v-if="form.hasErrors && form.errors['existing_pending_request']"
        :icon="TimesCircle"
        :description="form.errors['existing_pending_request'].message"
        primaryButtonLabel="Edit existing request"
        @onPrimaryClick="router.navigate({ url: form.errors['existing_pending_request'].href })"
        title="Submission error"
        color="danger"
        variant="soft"
        alignment="vertical"
        hideClose
      />
      <Alert
        v-else
        :icon="TimesCircle"
        description="Please correct the validation errors"
        title="Validation errors"
        color="danger"
        variant="soft"
      />
    </template>
  </Dialog>
  <DangerDialog
    title="Discard Changes?"
    message="Your changes will not be saved."
    :isOpen="isCancelDialogOpen"
    cancelButtonLabel="Stay on this page"
    confirmButtonLabel="Discard changes"
    @onCancel="() => toggleCancelDialog()"
    @onClose="() => toggleCancelDialog()"
    @onConfirm="() => handleCancelDialogConfirm()"
  />
</template>
